import React, { useMemo } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Legend
} from 'recharts';
import { useGetReferralTrafficQuery } from 'store/services/admin/analyticsApi';
import { AnalyticsPeriod } from 'types/interfaces/admin/analytics';
import TimeRangeDropdown from './TimeRangeDropdown';

interface Props {
  period: AnalyticsPeriod;
  onPeriodChange: (period: AnalyticsPeriod) => void;
}

const REFERRAL_COLORS = [
  '#8409D3',
  '#8D37A7',
  '#C769EB',
  '#DD5BEA',
  '#E02CDC',
  '#9941B4'
];

const ReferralTrafficChart: React.FC<Props> = ({ period, onPeriodChange }) => {
  const { data, isLoading, error } = useGetReferralTrafficQuery({ period });

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Transform data for Recharts: flatten sources into individual keys
  const chartData = useMemo(() => {
    if (!data?.data) return [];
    return data.data.map((point) => ({
      date: point.date,
      ...point.sources
    }));
  }, [data]);

  const sources = data?.allSources || [];

  return (
    <div className="chart-card">
      <div className="chart-header">
        <div className="chart-header-left">
          <h2>Referral Traffic</h2>
          <p className="chart-subtitle">Accounts created by marketing campaigns</p>
        </div>
        <div className="chart-header-right">
          <div className="chart-legend-multi">
            {sources.slice(0, 3).map((source, index) => (
              <div key={source} className="chart-legend">
                <span
                  className="legend-dot"
                  style={{ backgroundColor: REFERRAL_COLORS[index % REFERRAL_COLORS.length] }}
                />
                <span className="legend-label">{source}</span>
              </div>
            ))}
            {sources.length > 3 && (
              <span className="legend-label">+{sources.length - 3} more</span>
            )}
          </div>
          <TimeRangeDropdown value={period} onChange={onPeriodChange} />
        </div>
      </div>

      <div className="chart-container">
        {isLoading ? (
          <div className="chart-loading">Loading...</div>
        ) : error ? (
          <div className="chart-error">Failed to load data</div>
        ) : data ? (
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#2a2a2a" vertical={false} />
              <XAxis
                dataKey="date"
                tickFormatter={formatDate}
                stroke="#666"
                tick={{ fill: '#999', fontSize: 12 }}
                axisLine={{ stroke: '#2a2a2a' }}
                tickLine={false}
              />
              <YAxis
                stroke="#666"
                tick={{ fill: '#999', fontSize: 12 }}
                axisLine={{ stroke: '#2a2a2a' }}
                tickLine={false}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: '#1a1a1a',
                  border: '1px solid #2a2a2a',
                  borderRadius: '8px',
                  color: '#fff'
                }}
                labelFormatter={(label) => formatDate(label)}
              />
              <Legend
                wrapperStyle={{ paddingTop: '10px' }}
                formatter={(value) => <span style={{ color: '#999' }}>{value}</span>}
              />
              {sources.map((source, index) => (
                <Line
                  key={source}
                  type="monotone"
                  dataKey={source}
                  stroke={REFERRAL_COLORS[index % REFERRAL_COLORS.length]}
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 4 }}
                  name={source}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        ) : null}
      </div>

      {data && (
        <div className="chart-summary">
          Total signups by source: {Object.entries(data.totalBySource)
            .map(([source, count]) => `${source}: ${count.toLocaleString()}`)
            .join(' | ')}
        </div>
      )}
    </div>
  );
};

export default ReferralTrafficChart;
